import Icon from '@/components/ui/AppIcon';

interface VisaApplication {
  id: string;
  applicationType: string;
  destination: string;
  currentStatus: string;
  submittedDate: string;
  estimatedCompletionDate: string;
}

interface TimelineMilestone {
  id: string;
  title: string;
  description: string;
  date: string;
  status: 'completed' | 'current' | 'upcoming';
  icon: string;
}

interface VisaStatusTimelineProps {
  application: VisaApplication;
}

export default function VisaStatusTimeline({ application }: VisaStatusTimelineProps) {
  const milestones: TimelineMilestone[] = [
    {
      id: 'milestone-1',
      title: 'Application Submitted',
      description: 'Your visa application has been received by the embassy',
      date: application.submittedDate,
      status: 'completed',
      icon: 'DocumentCheckIcon'
    },
    {
      id: 'milestone-2',
      title: 'Document Verification',
      description: 'Embassy is verifying your submitted documents',
      date: new Date(new Date(application.submittedDate).getTime() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'completed',
      icon: 'DocumentMagnifyingGlassIcon'
    },
    {
      id: 'milestone-3',
      title: 'Background Check',
      description: 'Security and background verification in progress',
      date: new Date(new Date(application.submittedDate).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'current',
      icon: 'ShieldCheckIcon'
    },
    {
      id: 'milestone-4',
      title: 'Interview Scheduling',
      description: 'Embassy will schedule your visa interview',
      date: new Date(new Date(application.submittedDate).getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'upcoming',
      icon: 'UserGroupIcon'
    },
    {
      id: 'milestone-5',
      title: 'Final Decision',
      description: 'Embassy will make the final decision on your application',
      date: application.estimatedCompletionDate,
      status: 'upcoming',
      icon: 'CheckBadgeIcon'
    }
  ];

  const statusColors = {
    completed: 'bg-trust-green text-white',
    current: 'bg-primary text-white animate-pulse',
    upcoming: 'bg-muted text-muted-foreground'
  };

  return (
    <div className="bg-white rounded-xl shadow-brand p-6 border-2 border-border">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-bold text-xl text-foreground">
          Processing Timeline
        </h3>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="ClockIcon" size={16} />
          <span>Est. completion: {new Date(application.estimatedCompletionDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
        </div>
      </div>

      <div className="space-y-6">
        {milestones.map((milestone, index) => (
          <div key={milestone.id} className="relative">
            {index !== milestones.length - 1 && (
              <div className={`absolute left-5 top-12 bottom-0 w-0.5 ${
                milestone.status === 'completed' ? 'bg-trust-green' : 'bg-border'
              }`} />
            )}
            
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${statusColors[milestone.status]}`}>
                <Icon name={milestone.icon as any} size={20} />
              </div>
              
              <div className="flex-1 pb-6">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      {milestone.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">{milestone.description}</p>
                  </div>
                  <span className="text-xs font-medium text-primary whitespace-nowrap ml-4">
                    {new Date(milestone.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                {milestone.status === 'current' && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-primary font-medium">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                    Currently in progress
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}